import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTextosDaLoja } from '@/construtor/textos';
import { toast } from 'sonner';
import { AlertTriangle, Check, Copy, Handshake, Info } from 'lucide-react';
import PageHero from '@/components/common/PageHero';
import { PageNotFound } from '@/components/common/PageNotFound';
import { PartnerSalesChart } from '@/components/partner/PartnerSalesChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { useMyPartner } from '@/hooks/useMyPartner';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { day, lastDays, money, rate } from '@/lib/partner/format';
import {
  partnerService,
  type MyCommission,
  type MyPartnerReport,
  type MyPayout,
  type Page,
} from '@/lib/services/partner.service';

import { Caixa } from '@/construtor/caixa';
const PERIODOS = ['7', '30', '90', '365'] as const;
const POR_PAGINA = 20;

/**
 * Área do parceiro: o jogador dono de um cupom acompanha as vendas e a comissão.
 *
 * Só aparece para parceiro. Para quem não é, a página é a mesma "não
 * encontrada" de qualquer URL errada; a API também responde 404, então não há
 * o que ver aqui mesmo digitando o endereço.
 */
export default function PartnerPage() {
  const { t, i18n } = useTextosDaLoja();
  const language = i18n.language;
  const { isAuthenticated } = useAuth();
  const { store } = useStore();
  const { partner, loading, error } = useMyPartner();
  const { copy } = useCopyToClipboard();
  const [copiado, setCopiado] = useState<string | null>(null);

  const [periodo, setPeriodo] = useState<(typeof PERIODOS)[number]>('30');
  const [report, setReport] = useState<MyPartnerReport | null>(null);
  const [vendas, setVendas] = useState<Page<MyCommission> | null>(null);
  const [pagina, setPagina] = useState(1);
  const [pagamentos, setPagamentos] = useState<MyPayout[]>([]);
  const [falhou, setFalhou] = useState(false);

  const storeId = store?.id;

  const carregar = useCallback(async () => {
    if (!storeId || !partner) return;
    try {
      const [r, v, p] = await Promise.all([
        partnerService.report(storeId, lastDays(Number(periodo))),
        partnerService.commissions(storeId, { page: pagina, limit: POR_PAGINA }),
        partnerService.payouts(storeId, { limit: 20 }),
      ]);
      setReport(r);
      setVendas(v);
      setPagamentos(p.items);
      setFalhou(false);
    } catch {
      setFalhou(true);
    }
  }, [storeId, partner, periodo, pagina]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!loading && !error && !partner) return <PageNotFound />;

  const copiar = async (codigo: string) => {
    if (await copy(codigo)) {
      setCopiado(codigo);
      toast.success(t('store.partner.copied'));
      setTimeout(() => setCopiado(null), 2000);
    }
  };

  const saldo = report?.balance;
  const paginas = vendas ? Math.max(1, Math.ceil(vendas.total / POR_PAGINA)) : 1;

  return (
    <div className="min-h-screen bg-background pb-12">
      <PageHero title={t('store.partner.title')} breadcrumbs={[{ label: t('store.partner.title') }]} />

      <div className="container mx-auto space-y-6 px-4 py-8 md:px-6 lg:px-8">
        {(error || falhou) && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('store.partner.load_error')}</AlertDescription>
          </Alert>
        )}

        {partner && (
          <>
            <Caixa id="parceiro.cabecalho">
            <div className="flex flex-wrap items-center gap-3">
              <Handshake className="h-6 w-6 text-primary" />
              <div>
                <p className="text-lg font-semibold text-foreground">{partner.nick}</p>
                <p className="text-sm text-muted-foreground">
                  {t('store.partner.since', { date: day(partner.since, language) })}
                </p>
              </div>
            </div>
            </Caixa>

            {partner.status === 'SUSPENDED' && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{t('store.partner.suspended')}</AlertDescription>
              </Alert>
            )}

            <Caixa id="parceiro.cupons" miolo>
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base">{t('store.partner.coupons_title')}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                {partner.coupons.map((c) => (
                  <Caixa id="parceiro.cupons.cupom" repetida key={c.code}>
                  <div key={c.code} className="flex flex-col gap-2 rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-lg font-bold tracking-wide text-foreground">{c.code}</span>
                      <div className="flex items-center gap-2">
                        {!c.isActive && <Badge variant="secondary">{t('store.partner.coupon_inactive')}</Badge>}
                        <Button variant="outline" size="sm" onClick={() => copiar(c.code)} aria-label={t('store.partner.copy')}>
                          {copiado === c.code ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('store.partner.coupon_discount', { discount: rate(c.discountType, c.discountAmount, language) })}
                    </p>
                    <p className="text-sm text-foreground">
                      {t(c.includesCredits ? 'store.partner.coupon_commission_credits' : 'store.partner.coupon_commission', {
                        rate: rate(c.commissionType, c.commissionValue, language),
                      })}
                    </p>
                  </div>
                  </Caixa>
                ))}
              </CardContent>
            </Card>
            </Caixa>

            <Caixa id="parceiro.periodo">
            <Tabs value={periodo} onValueChange={(v) => { setPeriodo(v as (typeof PERIODOS)[number]); }}>
              <TabsList>
                {PERIODOS.map((p) => (
                  <TabsTrigger key={p} value={p}>
                    {t(`store.partner.period_${p}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            </Caixa>

            {report && saldo && (
              <Caixa id="parceiro.resumo">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
                {[
                  {
                    label: t('store.partner.kpi_sales'),
                    value: String(report.sales),
                    hint: report.reversedSales > 0 ? t('store.partner.kpi_reversed', { count: report.reversedSales }) : undefined,
                  },
                  { label: t('store.partner.kpi_revenue'), value: money(report.revenue, language) },
                  { label: t('store.partner.kpi_commission'), value: money(report.commission, language) },
                  {
                    label: t('store.partner.kpi_pending'),
                    value: money(saldo.pending, language),
                    hint: t('store.partner.kpi_pending_hint', { days: partner.holdDays }),
                  },
                  {
                    label: t('store.partner.kpi_available'),
                    value: money(saldo.available, language),
                    hint: saldo.available < 0 ? t('store.partner.kpi_available_negative') : t('store.partner.kpi_available_hint'),
                    testId: 'partner-available',
                  },
                  { label: t('store.partner.kpi_paid'), value: money(saldo.paid, language) },
                ].map((k) => (
                  <Caixa id="parceiro.resumo.numero" repetida key={k.label}>
                  <Card key={k.label} className="border-border bg-card">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">{k.label}</p>
                      <p className="mt-1 text-xl font-semibold text-foreground" data-testid={k.testId}>
                        {k.value}
                      </p>
                      {k.hint && <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>}
                    </CardContent>
                  </Card>
                  </Caixa>
                ))}
              </div>
              </Caixa>
            )}

            {report && (
              <Caixa id="parceiro.grafico" miolo>
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="text-base">{t('store.partner.chart_title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <PartnerSalesChart daily={report.daily} />
                </CardContent>
              </Card>
              </Caixa>
            )}

            <Caixa id="parceiro.vendas" miolo>
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base">{t('store.partner.sales_title')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!vendas || vendas.items.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">{t('store.partner.sales_empty')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('store.partner.col_date')}</TableHead>
                          <TableHead>{t('store.partner.col_buyer')}</TableHead>
                          <TableHead>{t('store.partner.col_coupon')}</TableHead>
                          <TableHead className="text-right">{t('store.partner.col_sale')}</TableHead>
                          <TableHead className="text-right">{t('store.partner.col_commission')}</TableHead>
                          <TableHead>{t('store.partner.col_state')}</TableHead>
                          <TableHead>{t('store.partner.col_available_at')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendas.items.map((v) => (
                          <Caixa id="parceiro.vendas.linha" repetida key={v.id}>
                          <TableRow key={v.id}>
                            <TableCell>{day(v.createdAt, language)}</TableCell>
                            <TableCell>
                              {v.kind === 'REVERSAL' ? (
                                <span className="text-muted-foreground">{t('store.partner.reversal')}</span>
                              ) : (
                                v.buyerNick ?? '-'
                              )}
                            </TableCell>
                            <TableCell className="font-mono">{v.couponCode}</TableCell>
                            <TableCell className="text-right">{money(v.baseAmount, language)}</TableCell>
                            <TableCell className={`text-right font-medium ${v.amount < 0 ? 'text-destructive' : ''}`}>
                              {money(v.amount, language)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{t(`store.partner.state_${v.state}`)}</Badge>
                            </TableCell>
                            <TableCell>{day(v.availableAt, language)}</TableCell>
                          </TableRow>
                          </Caixa>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {vendas && vendas.total > POR_PAGINA && (
                  <div className="flex items-center justify-end gap-2 p-4 text-sm">
                    <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                      {t('store.partner.prev')}
                    </Button>
                    <span className="text-muted-foreground">{t('store.partner.page', { page: pagina, pages: paginas })}</span>
                    <Button variant="outline" size="sm" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
                      {t('store.partner.next')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            </Caixa>

            <Caixa id="parceiro.pagamentos" miolo>
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base">{t('store.partner.payouts_title')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {pagamentos.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">{t('store.partner.payouts_empty')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('store.partner.col_date')}</TableHead>
                          <TableHead className="text-right">{t('store.partner.col_amount')}</TableHead>
                          <TableHead>{t('store.partner.col_method')}</TableHead>
                          <TableHead>{t('store.partner.col_reference')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagamentos.map((p) => (
                          <Caixa id="parceiro.pagamentos.linha" repetida key={p.id}>
                          <TableRow key={p.id}>
                            <TableCell>{day(p.createdAt, language)}</TableCell>
                            <TableCell className="text-right font-medium">{money(p.amount, language)}</TableCell>
                            <TableCell>{t(`store.partner.method_${p.method}`)}</TableCell>
                            <TableCell className="max-w-xs truncate">{p.reference ?? '-'}</TableCell>
                          </TableRow>
                          </Caixa>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
            </Caixa>

            <Caixa id="parceiro.como" miolo>
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Info className="h-4 w-4 text-primary" />
                  {t('store.partner.how_title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>{t('store.partner.how_1')}</p>
                <p>{t('store.partner.how_2', { days: partner.holdDays })}</p>
                <p>{t('store.partner.how_3')}</p>
              </CardContent>
            </Card>
            </Caixa>
          </>
        )}
      </div>
    </div>
  );
}
